/* ==========================================================================
   gba-tab-carousel.js
   Tab Collection Carousel — GiftBasketAustralia
   --------------------------------------------------------------------------
   Pure vanilla JS, no dependencies.
   Loaded with `defer` — runs after HTML is parsed.
   Handles:
     • Tab switching (click + keyboard navigation per ARIA tabs pattern)
     • Tab bar scroll arrows (prev / next)
     • Tab bar arrow visibility sync
     • Dawn slider-component reinit after tab reveal
     • Shopify theme editor events
   ~1.8 KB minified.
   ========================================================================== */

(function () {
  'use strict';

  /* ── GbaTabCarousel class ─────────────────────────────────────────────── */
  class GbaTabCarousel {
    constructor(el) {
      this.el        = el;
      this.tabs      = Array.from(el.querySelectorAll('[role="tab"]'));
      this.track     = el.querySelector('[data-gba-tabs-track]');
      this.prevArrow = el.querySelector('[data-gba-tabs-prev]');
      this.nextArrow = el.querySelector('[data-gba-tabs-next]');
      this._bound    = {};           // store named refs for cleanup
      this._init();
    }

    _init() {
      /* Tab click */
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (this._bound['click_' + tab.id] = () => this._activate(tab)));
      });

      /* Arrow button clicks — scroll the tab track */
      if (this.prevArrow) {
        this.prevArrow.addEventListener('click', (this._bound.prev = () => {
          if (this.prevArrow.disabled) return;
          this.track.scrollBy({ left: -240, behavior: 'smooth' });
        }));
      }
      if (this.nextArrow) {
         this.nextArrow.addEventListener('click', (this._bound.next = () => {
          if (this.nextArrow.disabled) return;
          this.track.scrollBy({ left: 240, behavior: 'smooth' });
        }));
      }

      /* Update arrow disabled state on scroll */
      if (this.track) {
        this.track.addEventListener('scroll', (this._bound.scroll = () => this._syncArrows()), { passive: true });
        this._syncArrows();          /* set initial state */

        if (typeof ResizeObserver !== 'undefined') {
          this._ro = new ResizeObserver(() => this._syncArrows());
          this._ro.observe(this.track);
          /* Also observe the tabs list itself — its width drives overflow */
          const tabsList = this.track.querySelector('[role="tablist"]');
          if (tabsList) this._ro.observe(tabsList);
        } else {
          /*
           * Fallback for browsers without ResizeObserver (very old):
           * re-sync on window load (all resources ready) and again after
           * a short delay to catch any deferred CSS paint.
           */
          window.addEventListener('load', () => this._syncArrows());
          setTimeout(() => this._syncArrows(), 300);
        }
      }

      /* Keyboard navigation — ARIA tabs pattern */
      this.el.addEventListener('keydown', (this._bound.keydown = e => this._onKeydown(e)));
    }

    /* ── Activate a tab ─────────────────────────────────────────────────── */
    _activate(tab) {
      /* Skip if already active */
      if (tab.getAttribute('aria-selected') === 'true') return;

      /* Deactivate all tabs */
      this.tabs.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });

      /* Hide all panels */
      const panelIds = this.tabs.map(t => t.getAttribute('aria-controls'));
      panelIds.forEach(id => {
        const panel = document.getElementById(id);
        if (panel) panel.hidden = true;
      });

      /* Activate selected tab */
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      /* Show selected panel */
      const panelId = tab.getAttribute('aria-controls');
      const panel   = document.getElementById(panelId);
      if (panel) {
        panel.hidden = false;

        /*
         * Dawn's slider-component listens for `window resize` to recalculate
         * button visibility. We dispatch it in the next frame, after the
         * browser has painted the now-visible panel with real dimensions.
         */
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }

      /* Scroll the clicked tab into view inside the tab track */
      tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }

    /* ── Sync arrow disabled states ─────────────────────────────────────── */
    _syncArrows() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (this.prevArrow) this.prevArrow.disabled = scrollLeft <= 0;
      if (this.nextArrow) this.nextArrow.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    }

    /* ── Keyboard navigation (ARIA tabs pattern) ─────────────────────────── */
    _onKeydown(e) {
      const tab = e.target.closest('[role="tab"]');
      if (!tab) return;

      const idx  = this.tabs.indexOf(tab);
      const last = this.tabs.length - 1;
      let next;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = this.tabs[(idx + 1) % this.tabs.length];
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = this.tabs[(idx - 1 + this.tabs.length) % this.tabs.length];
          break;
        case 'Home':
          next = this.tabs[0];
          break;
        case 'End':
          next = this.tabs[last];
          break;
        default:
          return; /* don't prevent default for other keys */
      }

      e.preventDefault();
      next.focus();
      this._activate(next);
    }
  }

  /* ── Bootstrap ────────────────────────────────────────────────────────── */
  function initAll() {
    document.querySelectorAll('[data-gba-tab-carousel]').forEach(el => {
      /* Avoid double-init */
      if (el._gbaTabCarousel) return;
      el._gbaTabCarousel = new GbaTabCarousel(el);
    });
  }

  /* Script is `defer` so DOM is already parsed — but guard just in case */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ── Shopify Theme Editor events ─────────────────────────────────────── */

  /* Section reloaded in editor */
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('[data-gba-tab-carousel]');
    if (el) {
      el._gbaTabCarousel = null; /* reset so initAll can re-init */
      initAll();
    }
  });

  /* Block selected in editor (e.g. merchant clicks a tab block) */
  document.addEventListener('shopify:block:select', e => {
    const el      = e.target.closest('[data-gba-tab-carousel]');
    const tabBtn  = e.target.querySelector('[role="tab"]');
    if (el && el._gbaTabCarousel && tabBtn) {
      el._gbaTabCarousel._activate(tabBtn);
    }
  });

  document.querySelectorAll('.moreless-button').forEach(function (btn) {
  btn.addEventListener('click', function () {
    var moreText = btn.previousElementSibling.classList.contains('moretext')
      ? btn.previousElementSibling
      : btn.parentElement.querySelector('.moretext');

    moreText.classList.toggle('show');
    btn.textContent = moreText.classList.contains('show') ? 'Read less' : 'Read more';
  });
});



//***************************** custom cart cyo *************************************************************************//

function removeCYOProductHrefs() {
  const cartItems = document.querySelectorAll('.t4s-mini_cart__item, [data-cart-item], .t4s-page_cart__infos');

  cartItems.forEach(item => {
    const titleLink = item.querySelector('.t4s-mini_cart__title, .t4s-page_cart__title');
    const imageLink = item.querySelector('.t4s-mini_cart__img, .t4s-page_cart__img');
    const hasBoxProperty = item.querySelector('.t4s-product-details__item-label strong');

    if (titleLink) {
      const titleText = titleLink.textContent.trim();
      const isCYO = titleText.startsWith('CYO');
      const hasBox = hasBoxProperty && hasBoxProperty.textContent.trim() === 'Box:';

      if (isCYO || hasBox) {
        if (titleLink.hasAttribute('href')) {
          titleLink.removeAttribute('href');
          titleLink.style.cursor = 'default';
          titleLink.style.textDecoration = 'none';
          titleLink.style.pointerEvents = 'none';
        }
        if (imageLink && imageLink.hasAttribute('href')) {
          imageLink.removeAttribute('href');
          imageLink.style.cursor = 'default';
          imageLink.style.pointerEvents = 'none';
        }
      }
    }
  });
}

// Prevent clicks on CYO product links (backup protection)
document.addEventListener('click', function (e) {
  const titleLink = e.target.closest('.t4s-mini_cart__title, .t4s-page_cart__title');
  const imageLink = e.target.closest('.t4s-mini_cart__img, .t4s-page_cart__img');

  if (titleLink || imageLink) {
    const cartItem = e.target.closest('.t4s-mini_cart__item, [data-cart-item], .t4s-page_cart__infos');
    if (cartItem) {
      const title = cartItem.querySelector('.t4s-mini_cart__title, .t4s-page_cart__title');
      const hasBoxProperty = cartItem.querySelector('.t4s-product-details__item-label strong');
      if (title) {
        const titleText = title.textContent.trim();
        const isCYO = titleText.startsWith('CYO');
        const hasBox = hasBoxProperty && hasBoxProperty.textContent.trim() === 'Box:';
        if (isCYO || hasBox) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    }
  }
}, true);

document.addEventListener('DOMContentLoaded', removeCYOProductHrefs);
document.addEventListener('DOMContentLoaded', function () {
  setTimeout(removeCYOProductHrefs, 100);
  setTimeout(removeCYOProductHrefs, 500);
  setTimeout(removeCYOProductHrefs, 1000);
});
document.addEventListener('cart:updated', removeCYOProductHrefs);
document.addEventListener('cart:refresh', removeCYOProductHrefs);

// Cart container observer — scoped, not body-wide
const cartContainer = document.querySelector('[data-cart-items], .t4s-mini_cart__items');
if (cartContainer) {
  new MutationObserver(removeCYOProductHrefs).observe(cartContainer, { childList: true, subtree: true });
}

const cartPage = document.querySelector('.t4s-page_cart, .cart-page, #CartPage');
if (cartPage) {
  new MutationObserver(removeCYOProductHrefs).observe(cartPage, { childList: true, subtree: true });
}

const drawerObserver = new MutationObserver(function (mutations) {
  mutations.forEach(function (mutation) {
    if (mutation.attributeName === 'class' || mutation.attributeName === 'style' || mutation.attributeName === 'hidden') {
      setTimeout(removeCYOProductHrefs, 50);
      setTimeout(removeCYOProductHrefs, 200);
    }
  });
});

document.querySelectorAll('.t4s-mini_cart, #CartDrawer, [data-cart-drawer], .cart-drawer, .t4s-page_cart').forEach(function (wrapper) {
  drawerObserver.observe(wrapper, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
});

// Body class observer — attributes only, safe (won't fire on DOM changes)
new MutationObserver(function (mutations) {
  mutations.forEach(function (mutation) {
    if (mutation.attributeName === 'class') {
      const bodyClasses = document.body.className;
      if (bodyClasses.includes('cart') || bodyClasses.includes('drawer')) {
        setTimeout(removeCYOProductHrefs, 50);
      }
    }
  });
}).observe(document.body, { attributes: true, attributeFilter: ['class'] });

if (window.location.pathname.includes('/cart')) {
  setTimeout(removeCYOProductHrefs, 100);
  setTimeout(removeCYOProductHrefs, 500);
}

// custom cart cyo - end


//***************************** cyo bundle creator easify app js *************************************************************************//

(function () {
  const STEP = 10;

  // ─── Selectors (stable hooks, not the random module classes) ────────────────
  const GRID_SELECTOR   = '.cyo-product-grid-wapper';
  const CARD_SELECTOR   = '[data-product-id][data-variant-ids]';
  const TITLE_SELECTOR  = '.PRODUCT_CARD_TITLE_TS';
  const ACCORDION_SELECTOR = '[class*="accordionElement-module__accordionContentItem"]';

  const EXCLUDED_WORDS = ['cyo', 'voucher', 'egift', 'create your own'];

  const gridsState = new Map();
  let isPaused = false;

  function getTitleText(product) {
    const titleEl = product.querySelector(TITLE_SELECTOR);
    return titleEl ? titleEl.textContent.trim() : '';
  }

  function isTitleLoaded(product) {
    return getTitleText(product) !== '';
  }

  function isExcluded(product) {
    if (product.getAttribute('data-excluded') === 'true') return true;

    // Hidden by something else (not by this script)
    const inlineDisplay = product.style.display;
    if (!inlineDisplay) {
      if (window.getComputedStyle(product).display === 'none') return true;
    }

    const title = getTitleText(product).toLowerCase();
    if (!title) return false;
    return EXCLUDED_WORDS.some(word => title.includes(word));
  }

  function getAllProducts(gridWrapper) {
    return Array.from(gridWrapper.querySelectorAll(CARD_SELECTOR));
  }

  function getEligibleProducts(gridWrapper) {
    return getAllProducts(gridWrapper).filter(
      p => p.getAttribute('data-excluded') !== 'true'
    );
  }

  function hideExcludedProducts(gridWrapper) {
    getAllProducts(gridWrapper).forEach(product => {
      if (product.getAttribute('data-excluded') === 'true') return;
      if (isExcluded(product)) {
        product.style.display = 'none';
        product.setAttribute('data-excluded', 'true');
      }
    });
  }

  function applyVisibility(gridWrapper, visibleItems) {
    getEligibleProducts(gridWrapper).forEach((product, index) => {
      product.style.display = index < visibleItems ? '' : 'none';
    });
  }

  function updateButtonVisibility(gridWrapper, state) {
    if (!state.btnElement) return;
    const eligible = getEligibleProducts(gridWrapper);

    // Show button only if there are still hidden products
    state.btnElement.style.display =
      eligible.length > state.visibleItems ? 'block' : 'none';
  }

  function waitForTitles(gridWrapper, callback) {
    const maxWait = 10000;
    const interval = 100;
    let elapsed = 0;

    const check = setInterval(() => {
      const allProducts = getAllProducts(gridWrapper);
      const allTitlesLoaded =
        allProducts.length > 0 && allProducts.every(isTitleLoaded);

      elapsed += interval;
      if (allTitlesLoaded || elapsed >= maxWait) {
        clearInterval(check);
        callback();
      }
    }, interval);
  }

  function createLoadMoreButton(gridWrapper, state) {
    const btnWrapper = document.createElement('div');
    btnWrapper.className = 'cyo-load-more-wrapper';
    btnWrapper.style.cssText =
      'text-align:center; margin: 30px 0; width:100%; clear:both;';

    btnWrapper.innerHTML = `
      <button type="button" class="cyo-load-more-btn" style="cursor:pointer; padding: 12px 35px; z-index:9 !important; background:#000; color:#fff; border:none; border-radius:4px; font-weight:bold; font-size:16px;">
        Load More Products
      </button>`;

    const btn = btnWrapper.querySelector('button');

    btn.addEventListener('click', e => {
      e.preventDefault();
      if (btn.disabled) return;

      btn.disabled = true;
      isPaused = true;

      hideExcludedProducts(gridWrapper);
      state.visibleItems += STEP;
      applyVisibility(gridWrapper, state.visibleItems);

      btn.disabled = false;
      isPaused = false;
      updateButtonVisibility(gridWrapper, state);
    });

    return btnWrapper;
  }

  function initGrid(gridWrapper) {
    if (!gridsState.has(gridWrapper)) {
      gridsState.set(gridWrapper, {
        visibleItems: STEP,
        btnElement: null,
        waitingForTitles: false
      });
    }

    const state = gridsState.get(gridWrapper);

    if (state.waitingForTitles) return;
    state.waitingForTitles = true;

    waitForTitles(gridWrapper, () => {
      state.waitingForTitles = false;

      hideExcludedProducts(gridWrapper);

      const eligible = getEligibleProducts(gridWrapper);
      if (eligible.length === 0) return;

      applyVisibility(gridWrapper, state.visibleItems);

      // Add the button (or re-add it if the app re-rendered and removed it)
      const buttonMissing = !state.btnElement || !state.btnElement.isConnected;
      if (eligible.length > STEP && buttonMissing) {
        state.btnElement = createLoadMoreButton(gridWrapper, state);
        gridWrapper.after(state.btnElement);
      }

      updateButtonVisibility(gridWrapper, state);
    });
  }

  function initAll() {
    document.querySelectorAll(GRID_SELECTOR).forEach(initGrid);
  }

  // ─── MutationObserver ───────────────────────────────────────────────────────
  let debounceTimer;
  const observer = new MutationObserver(() => {
    if (isPaused) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(initAll, 200);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // ─── Start ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll);
  }
})();

// Add to box button trigger on product card
document.addEventListener('click', function (e) {
  const productCard = e.target.closest('.cyo-product-grid-wapper .productContent-module__card_product__Hh');
  if (!productCard) return;

  const isExcluded =
    e.target.closest('.cyo-product-grid-wapper .addToBoxButton-module__button_atc__zo') ||
    e.target.closest('.cyo-product-grid-wapper .productContent-module__icon_search_product__Lx') ||
    e.target.closest('.cyo-product-grid-wapper a') ||
    e.target.closest('.cyo-product-grid-wapper button');

  if (isExcluded) return;

  const addToBoxBtn = productCard.querySelector('.cyo-product-grid-wapper .addToBoxButton-module__button_atc_content__wU');
  if (addToBoxBtn) {
    addToBoxBtn.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
  }
});

document.querySelectorAll('#tb-bulk-radio input[type="radio"]').forEach(radio => {
  radio.addEventListener('mousedown', function (e) {
    if (this.checked) e.preventDefault();
  });
});

const nextBtnObserver = new MutationObserver(() => {
  const btn = document.querySelector('.review-summery .BOX_REVIEW_PAGE_CREATE_NEW_BOX_BUTTON_TS span');
  if (btn && btn.textContent === 'Create New Box') {
    btn.textContent = 'Create New';
  }
});

nextBtnObserver.observe(document.body, { childList: true, subtree: true });


// Bulk app - download button injection
// FIX: injected flag prevents DOM append from re-triggering observer
(function () {
  let injected = false;

  function injectDownloadButton() {
    if (injected) return;
    const multiAddress = document.getElementById('tb-bulk-multi-address');
    if (!multiAddress) return;
    if (multiAddress.querySelector('.multi-address-downlaod-wapper')) return;

    injected = true;

    const wrapper = document.createElement('div');
    wrapper.className = 'multi-address-downlaod-wapper';

    const link = document.createElement('a');
    link.href = 'https://cdn.shopify.com/s/files/1/0661/5757/6258/files/Gift_Basket_Australia_Corporate_Bulk_Order_Form_2026.xlsx?v=1788436636';
    link.className = 'cgb-download-template-btn';
    link.setAttribute('download', '');
    link.textContent = 'Download bulk order form (Excel)';

    wrapper.appendChild(link);
    multiAddress.appendChild(wrapper);
  }

  new MutationObserver(injectDownloadButton).observe(document.body, { childList: true, subtree: true });
  injectDownloadButton();
})();


// Next button configuration
// FIX: debounced observer + guard flag on each button prevents re-processing
(function () {
  function hideCYOProducts() {
    document.querySelectorAll('.gba-bulk-accodian .productContent-module__card_product__Hh[data-product-id]').forEach(function (card) {
      const titleEl = card.querySelector('.productContent-module__product_information_title__em');
      if (titleEl) {
        const titleText = titleEl.textContent.trim().toLowerCase();
        if (
          titleText.includes('cyo') ||
          titleText.includes('voucher') ||
          titleText.includes('eGift Card') ||
          titleText.includes('create your own')
        ) {
          card.style.display = 'none';
        }
      }
    });
  }

  function moveNextButtons() {
    const allButtons = document.querySelectorAll('.style1Header-module__item__0i');
    const buttonMap = [
      { text: 'Step 2 - Branding Options',          nextLabel: 'Next : Branding Options',         target: '#gba-accdian-item-wapper-2' },
      { text: 'Step 2.5 - Add a Photo & Message',   nextLabel: 'Next : Add a Photo & Message',     target: '#gba-accdian-item-wapper-3' },
      { text: 'Step 3 - Add Greeting Card Message', nextLabel: 'Next : Add Greeting Card Message', target: '#gba-accdian-item-wapper-4' },
      { text: 'Step 4 - Add Recipient Adresses',    nextLabel: 'Next : Add Recipient Adresses',    target: '#gba-accdian-item-wapper-5' },
    ];

    buttonMap.forEach(function ({ text, nextLabel, target }) {
      const button = Array.from(allButtons).find(btn => btn.textContent.trim().includes(text));
      const wrapper = document.querySelector(target);

      // Guard: skip if already moved — prevents re-triggering loop
      if (!button || !wrapper || button.classList.contains('gba-moved-next-btn')) return;

      button.classList.add('gba-moved-next-btn');
      const labelEl = button.querySelector('div');
      if (labelEl) labelEl.textContent = nextLabel;

      button.addEventListener('click', function () {
        setTimeout(function () {
          const scrollEl = document.querySelector(target);
          if (scrollEl) {
            const top = scrollEl.getBoundingClientRect().top + window.scrollY - 130;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 300);
      });

      const btnWrapper = document.createElement('div');
      btnWrapper.classList.add('gba-moved-next-btn-wapper');
      btnWrapper.appendChild(button.parentNode.removeChild(button));
      wrapper.appendChild(btnWrapper);
    });
  }

  function init() {
    hideCYOProducts();
    moveNextButtons();

    let debounce;
    new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        hideCYOProducts();
        moveNextButtons();
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


// Review summary body class toggle
// FIX: disconnect before toggling class, reconnect after — prevents self-triggering loop
(function () {
  let lastState = null;

  const observer = new MutationObserver(function () {
    const shouldAdd = !!document.getElementById('Review-bulk-order-header');
    if (shouldAdd === lastState) return; // no change, do nothing
    lastState = shouldAdd;
    observer.disconnect();
    document.body.classList.toggle('review-summery', shouldAdd);
    observer.observe(document.body, { childList: true, subtree: true });
  });

  // Initial run
  lastState = !!document.getElementById('Review-bulk-order-header');
  document.body.classList.toggle('review-summery', lastState);
  observer.observe(document.body, { childList: true, subtree: true });
})();

// cyo bundle creator easify app js - end


//***************************** Title trimming *************************************************************************//

function getMaxLength() {
  const width = window.innerWidth;
  if (width >= 1396) return 60;
  if (width >= 1000) return 40;
  if (width >= 768) return 35;
  return 30;
}

function truncateTitles() {
  const maxLength = getMaxLength();

  document.querySelectorAll('.t4s-product-title a').forEach(title => {
    // Store original text once
    if (!title.getAttribute('data-original-text')) {
      title.setAttribute('data-original-text', title.textContent.trim());
    }

    const text = title.getAttribute('data-original-text');
    const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

    // Only write to DOM if value changed — prevents observer re-triggering
    if (title.textContent !== truncated) {
      title.textContent = truncated;
    }
  });
}

// FIX: only react to addedNodes (new pagination content), ignore text/attribute mutations
(function () {
  let truncateDebounce;
  new MutationObserver(function (mutations) {
    const hasNewNodes = mutations.some(m => m.addedNodes.length > 0);
    if (!hasNewNodes) return;
    clearTimeout(truncateDebounce);
    truncateDebounce = setTimeout(truncateTitles, 100);
  }).observe(document.body, { childList: true, subtree: true });
})();

// Re-run on tab click
document.querySelectorAll('.t4s-tab-item a').forEach(tab => {
  tab.addEventListener('click', () => setTimeout(truncateTitles, 300));
});

// Run on load + resize
truncateTitles();
window.addEventListener('resize', truncateTitles);

})();

document.addEventListener('DOMContentLoaded', () => {
    const datePickerWrapper = document.querySelector('#date-picker-wrapper');
    const cartFooter = document.querySelector('.cart__footer');
    if (datePickerWrapper && cartFooter) {
      cartFooter.appendChild(datePickerWrapper);
    }
  });


(function () {
  function initReadMore() {
    document.querySelectorAll('.moreless-button').forEach(function (button) {

      if (button.dataset.readmoreReady === 'true') return;
      button.dataset.readmoreReady = 'true';

      button.addEventListener('click', function () {

        var moreText = button.parentElement.previousElementSibling;

        if (!moreText || !moreText.classList.contains('moretext')) {
          return;
        }

        var isHidden = moreText.style.display === 'none';

        moreText.style.display = isHidden ? 'block' : 'none';
        button.textContent = isHidden ? 'Read less' : 'Read more';

      });

    });
  }

  initReadMore();

  document.addEventListener('shopify:section:load', initReadMore);
})();
